import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
// Page list is rise, then the Weidner company theme, then this property's
// `src/pages.json`. Woodin Creek Village names and extra pages (Wine & Dine,
// Parking Map) live in the property layer so the nav can match the live site.
import basePagesData from '@vibesites/theme-rise/pages.json';
import companyPagesData from '@vibesites-weidner-apartment-homes/theme-weidner-classic-theme/pages.json';
import localPagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/**
 * Page aliases that should never appear in the main navigation.
 *
 * Weidner Rise override: `'home'` is intentionally absent. The rise base
 * theme excludes Home from the dynamic nav (the left-aligned logo already
 * doubles as the home affordance), but Weidner branding guidance asks for
 * an explicit "Home" link surfaced in the main nav row. Position `0`
 * keeps it as the leftmost link.
 *
 * Woodin Creek Village groups the remaining pages the way the live site
 * does: Find Your Home, Explore Our Apartment Homes, and Explore the
 * Village. Contact Us stays a top-level link. Penthouse sits under
 * Explore Our Apartment Homes.
 */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'hyperlocal',
  'floor-plans-layout',
  'virtual-tour',
  'interactive-leasing-map',
]);

interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Optional link target for external / portal destinations. */
  target?: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`,
      or via synthetic nav groups. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
}

/**
 * Placeholder top-level labels that are not real pages. Children are
 * resolved from pages.json aliases in the order listed here.
 *
 * Top-level order (by first child position): Home → Find Your Home →
 * Explore Our Apartment Homes → Explore the Village → Contact Us.
 */
const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Find Your Home',
    aliases: ['floor-plans', 'interactive-map'],
  },
  {
    label: 'Explore Our Apartment Homes',
    aliases: [
      'amenities',
      'gallery',
      'schedule-a-tour',
      'future-residents-club',
      'pet-policy',
      'reviews',
      'penthouse',
    ],
  },
  {
    label: 'Explore the Village',
    aliases: ['neighborhood', 'wine-dine', 'parking-map'],
  },
];

const SYNTHETIC_GROUP_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((group) => group.aliases));

function navAnchorProps(link: NavLink): { target?: string; rel?: string } {
  if (!link.target || link.target === '_self') return {};
  return { target: link.target, rel: 'noopener noreferrer' };
}

function mergePageData(): Record<string, PageEntry> {
  const merged = { ...(basePagesData as Record<string, PageEntry>) };
  const layers = [
    companyPagesData as Record<string, Partial<PageEntry>>,
    localPagesData as Record<string, Partial<PageEntry>>,
  ];
  for (const overrides of layers) {
    for (const [alias, page] of Object.entries(overrides)) {
      merged[alias] = { ...(merged[alias] || {}), ...page } as PageEntry;
    }
  }
  return merged;
}

const pagesData = mergePageData();

interface PageSection {
  slot: string;
}

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'HeroSolidBG';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * Synthetic groups (Find Your Home, Explore Our Apartment Homes, Explore
 * the Village) are placeholder labels — not real pages. Interactive Site
 * Map (`interactive-map`) stays in Find Your Home and links to
 * `/interactive-leasing`, matching the live site.
 * A group with only one remaining child collapses to that bare link.
 */
function buildNavLinks(): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Second pass: attach any pages that still use a real pages.json parent.
  // Synthetic groups below own their aliases explicitly so they don't inherit
  // parent relationships like `interactive-map -> floor-plans`.
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  // Third pass: build the top-level nav in page position order, inserting
  // synthetic dropdowns where this Weidner header needs grouped labels that
  // don't correspond to real pages.
  for (const [alias, page] of eligible) {
    if (consumedAliases.has(alias)) continue;

    const group = SYNTHETIC_NAV_GROUPS.find((item) => item.aliases.includes(alias));
    if (group) {
      const children = group.aliases
        .map((childAlias) => linksByAlias.get(childAlias))
        .filter((link): link is NavLink => !!link);

      for (const child of children) {
        if (child.alias) consumedAliases.add(child.alias);
      }

      if (children.length === 1) {
        topLevel.push(children[0]);
        continue;
      }

      if (children.length > 1) {
        topLevel.push({ label: group.label, children });
      }

      continue;
    }

    const parent = page.parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    topLevel.push(linksByAlias.get(alias)!);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

// ============================================================================
// Fixed Weidner chrome palette
// ----------------------------------------------------------------------------
// Gold CTAs stay hard-coded so a property that maps primary→gold /
// secondary→black (common on Weidner sites) cannot turn Apply Now,
// Resident Access, office-hours day labels / clock icon, and the other
// util-row accents black. Same stance as Footer / SpecialOfferBanner.
// ============================================================================

const HEADER_CTA = '#B28C59';

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const payRentLinkDef: SlotGroupDefinition = {
  label: 'Pay Your Rent Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Pay Your Rent' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const maintenanceRequestLinkDef: SlotGroupDefinition = {
  label: 'Maintenance Request Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Submit a Maintenance Request' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const qualifyingCriteriaLinkDef: SlotGroupDefinition = {
  label: 'Qualifying Criteria Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://irp.cdn-website.com/c035667c/files/uploaded/Weidner_Qualifying_Criteria_Seattle.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// View Floor Plans CTA inside the mobile drawer. Hard-coded `/floor-plans`
// before; promoting to a slot group so the editor can retarget the
// button (e.g. to an off-site availability widget) and relabel it. Mirrors
// the schedule-tour link shape so the editor experience is consistent.
const viewFloorPlansLinkDef: SlotGroupDefinition = {
  label: 'View Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface OfficeHourRow {
  dayKey: string;
  label: string;
  hours: string;
}

/** JS Date.getDay() order (Sun=0) for open/closed status. */
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
/** Display order for the hover schedule (Mon → Sun), matching Tower 12. */
const OFFICE_HOURS_DISPLAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const OFFICE_DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function formatOfficeTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap])\.?m\.?/i, (_, p) => `${p.toUpperCase()}M`);
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function parseTimeToMinutes(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  const ampm = trimmed.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (ampm) {
    let hours = parseInt(ampm[1], 10);
    const minutes = parseInt(ampm[2], 10);
    const period = ampm[3].toLowerCase();
    if (period === 'pm' && hours < 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  }
  const military = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!military) return null;
  return parseInt(military[1], 10) * 60 + parseInt(military[2], 10);
}

function isOfficeCurrentlyOpen(hours: OfficeHour[] | null | undefined): boolean | null {
  if (!hours || hours.length === 0) return null;
  const now = new Date();
  const dayKey = DAY_KEYS[now.getDay()];
  const entry = hours.find((h) => (h.day || '').trim().toLowerCase().slice(0, 3) === dayKey);
  if (!entry) return false;
  const open = parseTimeToMinutes(entry.open_time);
  const close = parseTimeToMinutes(entry.close_time);
  if (open === null && close === null) return false;
  if (open === null || close === null) return null;
  const current = now.getHours() * 60 + now.getMinutes();
  if (close > open) return current >= open && current < close;
  // Overnight window (e.g. 10pm–2am)
  return current >= open || current < close;
}

/** Build Mon→Sun rows for the header hover panel (one row per day). */
function buildOfficeHoursRows(raw: OfficeHour[] | null | undefined): OfficeHourRow[] {
  if (!raw || raw.length === 0) return [];
  const byDay: Record<string, OfficeHour> = {};
  for (const entry of raw) {
    if (!entry.day) continue;
    const key = entry.day.trim().toLowerCase().slice(0, 3);
    if (!(key in OFFICE_DAY_LABELS)) continue;
    if (!(key in byDay)) byDay[key] = entry;
  }

  return OFFICE_HOURS_DISPLAY_ORDER.map((key) => {
    const entry = byDay[key];
    const closed =
      !entry ||
      ((!entry.open_time || !String(entry.open_time).trim()) &&
        (!entry.close_time || !String(entry.close_time).trim()));
    return {
      dayKey: key,
      label: OFFICE_DAY_LABELS[key],
      hours: closed
        ? 'Closed'
        : `${formatOfficeTime(entry!.open_time || '')} - ${formatOfficeTime(entry!.close_time || '')}`,
    };
  });
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Office Hours status + hover schedule (Tower 12)
// ============================================================================

function OfficeHoursStatus({
  officeOpen,
  rows,
}: {
  officeOpen: boolean | null;
  rows: OfficeHourRow[];
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasSchedule = rows.length > 0;

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="header-office-hours relative"
      onMouseEnter={() => hasSchedule && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="header-util-status"
        aria-expanded={hasSchedule ? open : undefined}
        aria-haspopup={hasSchedule ? 'dialog' : undefined}
        onFocus={() => hasSchedule && setOpen(true)}
        onClick={() => hasSchedule && setOpen((current) => !current)}
      >
        <ClockIcon className="w-3.5 h-3.5 header-util-status-icon" />
        <span>
          Office Hours
          {officeOpen === null ? '' : officeOpen ? ' - Open' : ' - Closed'}
        </span>
      </button>

      {hasSchedule ? (
        <div
          className={`header-office-hours-dropdown absolute right-0 top-full z-[80] mt-0 min-w-[260px] transition-all duration-200 ${
            open
              ? 'opacity-100 visible translate-y-0'
              : 'opacity-0 invisible -translate-y-1 pointer-events-none'
          }`}
          role="dialog"
          aria-label="Office hours"
        >
          <dl className="header-office-hours-list">
            {rows.map((row) => (
              <div key={row.dayKey} className="header-office-hours-row">
                <dt style={{ color: HEADER_CTA }}>{row.label}</dt>
                <dd>{row.hours}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        {...navAnchorProps(link)}
        className="header-nav-link font-body text-[13px] font-normal tracking-[0.14em] uppercase transition-colors py-2 whitespace-nowrap"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {link.href ? (
        <a
          href={link.href}
          {...navAnchorProps(link)}
          className="header-nav-link font-body text-[13px] font-normal tracking-[0.14em] uppercase transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
          aria-haspopup="true"
          aria-expanded={open}
          onFocus={() => setOpen(true)}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          onFocus={() => setOpen(true)}
          className="header-nav-link font-body text-[13px] font-normal tracking-[0.14em] uppercase transition-colors py-2 flex items-center gap-1 whitespace-nowrap bg-transparent"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        {link.children!.map((child) => (
          <a
            key={child.href || child.label}
            href={child.href}
            {...navAnchorProps(child)}
            className="header-dropdown-link block px-5 py-3 font-body text-[13px] font-normal uppercase tracking-[0.14em] whitespace-nowrap transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href || link.label}
            className="font-body text-[13px] font-normal tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href || link.label} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="header-nav-link font-body text-[13px] font-normal tracking-[0.14em] uppercase transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href || link.label}>
                  {link.href ? (
                    <a
                      href={link.href}
                      {...navAnchorProps(link)}
                      className="header-dropdown-link block px-5 py-3 font-body text-[13px] font-normal uppercase tracking-[0.14em] whitespace-nowrap transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <div className="header-dropdown-link block px-5 py-3 font-body text-[13px] font-normal uppercase tracking-[0.14em] whitespace-nowrap">
                      {link.label}
                    </div>
                  )}
                  {link.children?.map((child) => (
                    <a
                      key={child.href || child.label}
                      href={child.href}
                      {...navAnchorProps(child)}
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent (e.g. Find Your Home) gets
                      // pushed into overflow and we want its sub-items to
                      // remain reachable from the dropdown.
                      className="header-dropdown-link header-dropdown-link--muted block pl-9 pr-5 py-3 font-body text-[13px] font-normal uppercase tracking-[0.14em] whitespace-nowrap transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Hamburger drawer nav (Tower 12–style dark mega menu)
// ============================================================================

function formatMoney(raw: string | null | undefined): string | null {
  if (raw == null || String(raw).trim() === '') return null;
  const num = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return String(raw).trim();
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function isTruthyFlag(val: unknown): boolean {
  if (val === true || val === 1) return true;
  if (val == null || val === false) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function DrawerNavLink({
  link,
  onNavigate,
  depth = 0,
}: {
  link: NavLink;
  onNavigate: () => void;
  depth?: number;
}) {
  return (
    <a
      href={link.href || '#'}
      {...navAnchorProps(link)}
      onClick={onNavigate}
      className={`header-drawer-link block font-body uppercase tracking-[0.14em] transition-colors ${
        depth > 0
          ? 'header-drawer-link--child py-2.5 pl-5 pr-2 text-[12px] font-medium'
          : 'py-3 pr-2 text-[13px] font-normal'
      }`}
    >
      {link.label}
    </a>
  );
}

function DrawerNavItem({
  link,
  index,
  openGroups,
  onToggle,
  onNavigate,
}: {
  link: NavLink;
  index: number;
  openGroups: number[];
  onToggle: (index: number) => void;
  onNavigate: () => void;
}) {
  const hasChildren = !!(link.children && link.children.length);
  const isOpen = openGroups.includes(index);

  if (!hasChildren) {
    return <DrawerNavLink link={link} onNavigate={onNavigate} />;
  }

  return (
    <div className="header-drawer-group">
      <button
        type="button"
        onClick={() => onToggle(index)}
        className="header-drawer-link header-drawer-group-toggle w-full py-3 pr-1 font-body text-[13px] font-normal uppercase tracking-[0.14em] transition-colors flex items-center justify-between gap-3 bg-transparent"
        aria-expanded={isOpen}
      >
        <span>{link.label}</span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[40rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pb-2">
          {link.children!.map((child) => (
            <DrawerNavLink
              key={child.href || child.label}
              link={child}
              onNavigate={onNavigate}
              depth={1}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Office hours, phone and address block shown in the hamburger drawer. */
function DrawerContactInfo({
  phone,
  officeHoursRows,
  addressLink,
  directionsUrl,
}: {
  phone: string | null | undefined;
  officeHoursRows: OfficeHourRow[];
  addressLink: { address?: string; url?: string; _target?: string };
  directionsUrl: string;
}) {
  return (
    <div className="header-drawer-contact">
      <EditableCollectionBinding bindingPath="header.office_hours" label="Office Hours">
        {officeHoursRows.length > 0 ? (
          <div>
            <h3 className="header-drawer-preview-heading">Office Hours</h3>
            <dl className="header-drawer-hours">
              {officeHoursRows.map((row) => (
                <div key={row.dayKey} className="header-drawer-hours-row">
                  <dt>{row.label}</dt>
                  <dd>{row.hours}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </EditableCollectionBinding>

      <div className="header-drawer-contact-links">
        <a
          href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
          className="dniphone dniphonehref header-drawer-preview-copy header-drawer-inline-link flex items-center gap-2"
          aria-label="Call us"
        >
          <PhoneIcon className="w-4 h-4 flex-shrink-0 header-drawer-contact-icon" />
          <EditableToken
            path="header.phone"
            defaultField="property_call_tracking_phone"
            defaultValue="(555) 555-5555"
            as="span"
          >
            {formatPhone(phone)}
          </EditableToken>
        </a>
        <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
          <a
            href={directionsUrl}
            target={addressLink._target || '_blank'}
            rel="noopener noreferrer"
            className="header-drawer-preview-copy header-drawer-inline-link flex items-start gap-2"
          >
            <MapPinIcon className="w-4 h-4 flex-shrink-0 mt-0.5 header-drawer-contact-icon" />
            <span>{addressLink.address}</span>
          </a>
        </EditableSlotGroup>
      </div>
    </div>
  );
}

/** Fixed right-rail content for the hamburger mega-menu (Tower 12). */
function DrawerInfoPanel({
  phone,
  officeHoursRows,
  addressLink,
  directionsUrl,
  viewFloorPlansLink,
  qualifyingCriteriaLink,
  showQualifyingCriteria,
}: {
  phone: string | null | undefined;
  officeHoursRows: OfficeHourRow[];
  addressLink: { address?: string; url?: string; _target?: string };
  directionsUrl: string;
  viewFloorPlansLink: { url?: string; label?: string; _target?: string };
  qualifyingCriteriaLink: { url?: string; label?: string; _target?: string };
  showQualifyingCriteria: boolean;
}) {
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const maxPets = usePropertyField('property_max_pets') as string | null;
  const maxWeight = usePropertyField('property_max_pet_weight_limit') as string | null;
  const monthlyPetRent = usePropertyField('property_monthly_pet_rent') as string | null;
  const petRestrictions = usePropertyField('property_pet_restrictions') as string | null;
  const applicationFee = usePropertyField('property_application_fee') as string | null;

  const allowedPets = [
    isTruthyFlag(allowsDogs) && 'Dogs',
    isTruthyFlag(allowsCats) && 'Cats',
  ].filter(Boolean) as string[];

  const monthlyRentLabel = formatMoney(monthlyPetRent);
  const appFeeLabel = formatMoney(applicationFee);
  const hasWeightLimit = !!(maxWeight && String(maxWeight).trim() && String(maxWeight).trim() !== '0');
  const hasBreedNote = !!(petRestrictions && String(petRestrictions).trim());

  return (
    <div className="header-drawer-preview-body">
      <DrawerContactInfo
        phone={phone}
        officeHoursRows={officeHoursRows}
        addressLink={addressLink}
        directionsUrl={directionsUrl}
      />

      <h3 className="header-drawer-preview-heading mt-8">Our Pet Policy</h3>
      <ul className="header-drawer-preview-list">
        {monthlyRentLabel ? <li>Monthly Pet Rent: {monthlyRentLabel}</li> : null}
        {allowedPets.length > 0 ? <li>Allowed Pets: {allowedPets.join(' & ')}</li> : null}
        {maxPets ? <li>Pet Limit: {maxPets}</li> : null}
        <li>{hasWeightLimit ? `Weight limit: ${maxWeight}` : 'No weight restrictions'}</li>
        {hasBreedNote ? <li>Breed restrictions apply</li> : null}
      </ul>

      <div className="mt-8">
        <h3 className="header-drawer-preview-heading">Qualifying Criteria</h3>
        <p className="header-drawer-preview-copy mt-3">
          Touring: Government issued I.D. is required to tour.
        </p>
        <p className="header-drawer-preview-copy mt-3">
          Review our detailed Qualifying Criteria for income, credit and criminal screening{' '}
          {showQualifyingCriteria ? (
            <EditableSlotGroup groupPath="header.qualifying_criteria_link" definition={qualifyingCriteriaLinkDef}>
              <a
                href={qualifyingCriteriaLink.url || '#'}
                target={qualifyingCriteriaLink._target || '_blank'}
                rel="noopener noreferrer"
                className="header-drawer-inline-link header-drawer-inline-link--accent"
              >
                here
              </a>
            </EditableSlotGroup>
          ) : (
            <EditableSlotGroup groupPath="header.qualifying_criteria_link" definition={qualifyingCriteriaLinkDef}>
              <span className="sr-only">{qualifyingCriteriaLink.label || 'Qualifying Criteria'}</span>
            </EditableSlotGroup>
          )}
          .
        </p>
      </div>

      {(appFeeLabel || monthlyRentLabel) && (
        <div className="mt-8">
          <h3 className="header-drawer-preview-heading">Community Fees</h3>
          {appFeeLabel ? (
            <div className="mt-4">
              <p className="header-drawer-preview-subhead">One-Time Expenses</p>
              <p className="header-drawer-preview-copy">Application Fee: {appFeeLabel}</p>
            </div>
          ) : null}
          {monthlyRentLabel ? (
            <div className="mt-4">
              <p className="header-drawer-preview-subhead">Recurring Expenses</p>
              {isTruthyFlag(allowsCats) ? (
                <p className="header-drawer-preview-copy">Cat Rent {monthlyRentLabel}</p>
              ) : null}
              {isTruthyFlag(allowsDogs) ? (
                <p className="header-drawer-preview-copy">Dog Rent {monthlyRentLabel}</p>
              ) : null}
              {!isTruthyFlag(allowsCats) && !isTruthyFlag(allowsDogs) ? (
                <p className="header-drawer-preview-copy">Pet Rent {monthlyRentLabel}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
        <span className="sr-only">{viewFloorPlansLink.label || 'View Floor Plans'}</span>
      </EditableSlotGroup>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE (Tower 12–inspired): solid black utility bar + main nav.
// Utility row: company mark · phone · office-hours status | gold quick links.
// Main nav: hamburger + logo | centered page links | ghost Schedule CTA.
// Header stays dark (overlays the hero); hamburger opens a dark mega-menu drawer.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDrawerGroups, setOpenDrawerGroups] = useState<number[]>([]);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [officeOpen, setOfficeOpen] = useState<boolean | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Kept so interior-page CSS hooks that still key off HeroSolidBG remain
  // available if other styles need them; the header itself is always dark.
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });

  const officeHoursRows = useMemo(
    () => buildOfficeHoursRows(officeHours),
    [officeHours],
  );

  useEffect(() => {
    setOfficeOpen(isOfficeCurrentlyOpen(officeHours));
    const id = window.setInterval(() => {
      setOfficeOpen(isOfficeCurrentlyOpen(officeHours));
    }, 60_000);
    return () => window.clearInterval(id);
  }, [officeHours]);

  // Content tokens
  const phone = useContentToken('header.phone');
  // Slot groups — property logo always prefers the white mono mark against
  // the solid black header bar (Tower 12 treatment).
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const propertyName = usePropertyField('property_name');
  const logoImage = monoWhiteLogo || logoRaw.image;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const payRentLink = useSlotGroup('header.pay_rent_link', payRentLinkDef);
  const maintenanceRequestLink = useSlotGroup(
    'header.maintenance_request_link',
    maintenanceRequestLinkDef,
  );
  const residentPortalUrl = usePropertyField('property_resident_portal_url') as string | null | undefined;
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const qualifyingCriteriaLink = useSlotGroup('header.qualifying_criteria_link', qualifyingCriteriaLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const viewFloorPlansLink = useSlotGroup('header.view_floor_plans_link', viewFloorPlansLinkDef);

  const navLinks = useMemo(() => {
    const links = buildNavLinks();
    const contactGroup = links.find((link) => link.label === 'Contact' && link.children);

    if (!contactGroup?.children) return links;

    // Portal destinations — always listed under Contact so the dropdown
    // matches the Weidner nav IA even when a property hasn't set a URL yet.
    // Both pay rent and maintenance request open the resident portal.
    const portalUrl = (residentPortalUrl || '').toString().trim();
    const payUrl = (payRentLink.url || portalUrl || '').toString().trim();
    const maintenanceUrl = (maintenanceRequestLink.url || portalUrl || '')
      .toString()
      .trim();

    const extras: NavLink[] = [
      {
        label: payRentLink.label || 'Pay Your Rent',
        href: payUrl || '#',
        target: payRentLink._target || '_blank',
      },
      {
        label: maintenanceRequestLink.label || 'Submit a Maintenance Request',
        href: maintenanceUrl || '#',
        target: maintenanceRequestLink._target || '_blank',
      },
    ];

    return links.map((link) =>
      link.label === 'Contact' && link.children
        ? { ...link, children: [...link.children, ...extras] }
        : link,
    );
  }, [
    payRentLink.url,
    payRentLink.label,
    payRentLink._target,
    maintenanceRequestLink.url,
    maintenanceRequestLink.label,
    maintenanceRequestLink._target,
    residentPortalUrl,
  ]);
  const propertyAddressUrl = usePropertyAddressUrl();
  const directionsUrl = propertyAddressUrl || addressLink.url || '#';
  const showQualifyingCriteria = !!(qualifyingCriteriaLink.url && String(qualifyingCriteriaLink.url).trim());

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Measure header height for the mobile drawer offset
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll when the hamburger menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // The hamburger is hidden at lg+, so close the drawer if the viewport grows past it
  useEffect(() => {
    if (!menuOpen) return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const closeIfDesktop = () => {
      if (mql.matches) {
        setMenuOpen(false);
        setOpenDrawerGroups([]);
      }
    };
    closeIfDesktop();
    mql.addEventListener('change', closeIfDesktop);
    return () => mql.removeEventListener('change', closeIfDesktop);
  }, [menuOpen]);

  // Escape closes the hamburger menu on mobile
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setOpenDrawerGroups([]);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // When the drawer opens, expand all parent groups (Tower 12 default).
  useEffect(() => {
    if (!menuOpen) return;
    setOpenDrawerGroups(
      navLinks.reduce<number[]>((acc, link, index) => {
        if (link.children && link.children.length) acc.push(index);
        return acc;
      }, []),
    );
  }, [menuOpen, navLinks]);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenDrawerGroups([]);
  };

  const toggleDrawerGroup = (index: number) => {
    setOpenDrawerGroups((current) =>
      current.includes(index) ? current.filter((i) => i !== index) : [...current, index],
    );
  };


  return (
    <>
      {/* No spacer — solid black header overlays hero content (Tower 12) */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-[70] w-full header-tower${overSolidHero ? ' header-over-solid-hero' : ''}${menuOpen ? ' header-menu-open' : ''}`}
        style={{ top: 0, backgroundColor: '#0a0a0a' }}
      >
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — company · phone | hours · gold quick links     */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="px-4 sm:px-6 lg:px-10">
              <div className="header-util-inner">
                <div className="header-util-cluster min-w-0">
                  {addressLink.address ? (
                    <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                      <a
                        href={directionsUrl}
                        target={addressLink._target || '_blank'}
                        rel="noopener noreferrer"
                        className="header-util-link min-w-0"
                      >
                        <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{addressLink.address}</span>
                      </a>
                    </EditableSlotGroup>
                  ) : null}

                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref header-util-link"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>

                <div className="header-util-cluster flex-shrink-0">
                  <EditableCollectionBinding
                    bindingPath="header.office_hours"
                    label="Office Hours"
                    className="header-office-status"
                  >
                    <OfficeHoursStatus officeOpen={officeOpen} rows={officeHoursRows} />
                  </EditableCollectionBinding>

                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-accent"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-accent"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>

                  {/* Editable in the panel; rendered as Contact dropdown items. */}
                  <EditableSlotGroup groupPath="header.pay_rent_link" definition={payRentLinkDef}>
                    <span className="sr-only">{payRentLink.label || 'Pay Your Rent'}</span>
                  </EditableSlotGroup>

                  <EditableSlotGroup
                    groupPath="header.maintenance_request_link"
                    definition={maintenanceRequestLinkDef}
                  >
                    <span className="sr-only">
                      {maintenanceRequestLink.label || 'Submit a Maintenance Request'}
                    </span>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          <div className="header-util-row lg:hidden">
            <div className="px-4 sm:px-6">
              <div className="header-util-inner" style={{ height: '2.25rem' }}>
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref header-util-link header-mobile-util-link"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>

                <OfficeHoursStatus officeOpen={officeOpen} rows={officeHoursRows} />
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-10">
            <div className="flex items-center h-16 lg:h-[76px]">
              {/* Hamburger — left of logo below lg; hidden once the desktop top-bar nav shows */}
              <button
                type="button"
                onClick={() => {
                  if (menuOpen) setOpenDrawerGroups([]);
                  setMenuOpen(!menuOpen);
                }}
                className="relative w-10 h-10 flex lg:hidden items-center justify-center mr-1 flex-shrink-0"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="header-nav-menu"
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>

              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {logoImage ? (
                    <VibesitesImage
                      url={logoImage}
                      alt={(logoRaw as { alt?: string }).alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                      sizes="180px"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-white">
                      {propertyName || 'Home'}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              <DesktopNav navLinks={navLinks} />

              <div className="flex items-center gap-2.5 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-3.5 py-2.5 lg:px-5 lg:py-3 text-[10px] lg:text-[11px] font-medium tracking-[0.14em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <CalendarIcon className="relative z-10 w-3.5 h-3.5 hidden sm:block" />
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Visit'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

      </header>

      {/* ============================================================== */}
      {/* Dark mega-menu drawer (Tower 12–style)                          */}
      {/* Header stays above so logo + hamburger/X remain visible.        */}
      {/* ============================================================== */}
      <div
        className={`header-menu-backdrop fixed inset-0 z-[60] bg-black/55 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <div
        id="header-nav-menu"
        className={`header-menu-drawer fixed left-0 bottom-0 z-[61] w-[min(68rem,94vw)] overflow-hidden transition-transform duration-300 ease-out ${
          menuOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'
        }`}
        style={{ top: `${headerHeight}px` }}
        aria-hidden={!menuOpen}
      >
        <div className="header-menu-drawer-inner flex h-full min-h-0">
          <nav
            role="navigation"
            aria-label="Menu navigation"
            className="header-menu-nav flex-shrink-0 w-full max-w-full md:w-[17.5rem] xl:w-[19rem] overflow-y-auto px-6 sm:px-8 py-6 lg:py-10"
          >
            {navLinks.map((link, index) => (
              <DrawerNavItem
                key={link.href || link.label}
                link={link}
                index={index}
                openGroups={openDrawerGroups}
                onToggle={toggleDrawerGroup}
                onNavigate={closeMobileMenu}
              />
            ))}

            <div className="lg:hidden">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_blank'}
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="header-drawer-link header-drawer-link--accent block py-3 pr-2 font-body text-[13px] font-normal uppercase tracking-[0.14em] transition-colors"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_blank'}
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="header-drawer-link header-drawer-link--accent block py-3 pr-2 font-body text-[13px] font-normal uppercase tracking-[0.14em] transition-colors"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
            </div>

            <div className="lg:hidden mt-8 pt-6 border-t border-white/15 space-y-3">
              <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
                <a
                  href={viewFloorPlansLink.url || '/floor-plans'}
                  target={viewFloorPlansLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="header-drawer-cta block text-center"
                >
                  {viewFloorPlansLink.label || 'View Floor Plans'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="header-drawer-cta header-drawer-cta--ghost block text-center"
                >
                  {scheduleTourLink.label || 'Schedule a Visit'}
                </a>
              </EditableSlotGroup>
              <div className="md:hidden pt-5">
                <DrawerContactInfo
                  phone={phone}
                  officeHoursRows={officeHoursRows}
                  addressLink={addressLink}
                  directionsUrl={directionsUrl}
                />
              </div>
            </div>
          </nav>

          <div className="header-menu-preview hidden md:flex flex-1 min-w-0 overflow-y-auto px-8 lg:px-10 xl:px-14 py-6 lg:py-10 border-l border-white/10">
            <DrawerInfoPanel
              phone={phone}
              officeHoursRows={officeHoursRows}
              addressLink={addressLink}
              directionsUrl={directionsUrl}
              viewFloorPlansLink={viewFloorPlansLink}
              qualifyingCriteriaLink={qualifyingCriteriaLink}
              showQualifyingCriteria={showQualifyingCriteria}
            />
          </div>
        </div>
      </div>

      <style>{`
        .header-tower {
          color: #fff;
          box-shadow: 0 1px 0 rgba(255,255,255,0.08);
        }
        .header-bar {
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        .header-util-row {
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }

        /* Keep every util-row item on one vertical midline (wrappers + icons). */
        .header-util-row .header-util-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          height: 2.5rem;
        }
        .header-util-row .header-util-cluster {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          min-height: 100%;
        }
        .header-util-row .header-util-cluster > * {
          display: inline-flex;
          align-items: center;
          align-self: center;
          line-height: 1;
          min-height: 1rem;
        }
        .header-util-row .header-company-mark,
        .header-util-row .header-office-hours,
        .header-util-row .header-office-status {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          line-height: 1;
        }
        .header-util-row .header-office-hours,
        .header-util-row .header-office-status {
          gap: 0;
        }
        .header-util-row .header-company-mark img,
        .header-util-row .header-util-link svg,
        .header-util-row .header-util-status-icon {
          display: block;
          flex-shrink: 0;
        }

        .header-company-name {
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 400;
          letter-spacing: 0.04em;
          line-height: 1;
          color: rgba(255,255,255,0.85);
          white-space: nowrap;
        }

        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 500;
          letter-spacing: 0.02em;
          line-height: 1;
          color: rgba(255,255,255,0.9);
          text-decoration: underline;
          text-decoration-color: transparent;
          text-underline-offset: 4px;
          white-space: nowrap;
          transition: color 0.2s ease, text-decoration-color 0.2s ease;
        }
        .header-util-link:hover {
          color: #fff;
          text-decoration-color: currentColor;
        }
        .header-mobile-util-link {
          text-transform: none;
          letter-spacing: 0.02em;
        }

        .header-util-status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          line-height: 1;
          color: rgba(255,255,255,0.78);
          white-space: nowrap;
          cursor: default;
          border: 0;
          padding: 0;
          margin: 0;
          background: transparent;
          appearance: none;
          -webkit-appearance: none;
        }
        .header-util-status:hover,
        .header-office-hours:hover .header-util-status,
        .header-office-hours:focus-within .header-util-status {
          color: #fff;
        }
        .header-util-status-icon {
          color: ${HEADER_CTA};
        }

        /* Full weekly schedule — revealed on hover / focus / tap */
        .header-office-hours-dropdown {
          background: rgba(10, 10, 10, 0.96);
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
          padding: 0.35rem 0;
        }
        .header-office-hours-list {
          margin: 0;
        }
        .header-office-hours-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 1.75rem;
          padding: 0.55rem 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          line-height: 1.3;
        }
        .header-office-hours-row:last-child {
          border-bottom: 0;
        }
        .header-office-hours-row dt {
          color: ${HEADER_CTA};
          font-weight: 500;
          letter-spacing: 0.02em;
        }
        .header-office-hours-row dd {
          margin: 0;
          color: rgba(255, 255, 255, 0.92);
          font-weight: 400;
          text-align: right;
          white-space: nowrap;
        }
        @media (max-width: 1023px) {
          .header-office-hours-dropdown {
            left: auto;
            right: 0;
          }
        }

        .header-util-accent {
          display: inline-flex;
          align-items: center;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          line-height: 1;
          color: ${HEADER_CTA};
          white-space: nowrap;
          text-decoration: none;
          transition: color 0.2s ease, opacity 0.2s ease;
        }
        .header-util-accent:hover {
          color: color-mix(in srgb, ${HEADER_CTA} 80%, white);
        }

        .header-nav-link {
          color: rgba(255,255,255,0.92);
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 8px;
          transition: color 0.2s ease, text-decoration-color 0.25s ease;
        }
        .header-nav-link:hover {
          color: #fff;
          text-decoration-color: currentColor;
        }

        .nav-more-dropdown {
          background: #fff;
          width: max-content;
        }
        .header-dropdown-link {
          color: #000 !important;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          white-space: nowrap;
        }
        .header-dropdown-link:hover {
          color: #fff !important;
          background: #000;
        }
        .header-dropdown-link--muted {
          color: rgba(0,0,0,0.65) !important;
        }
        .header-dropdown-link--muted:hover {
          color: #fff !important;
        }

        .nav-cta-btn {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.85);
          border-radius: 0;
          color: #fff;
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          box-shadow: none;
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }

        .header-menu-drawer {
          will-change: transform;
          background: rgba(12, 12, 12, 0.94);
          color: #fff;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 8px 0 40px rgba(0, 0, 0, 0.35);
        }
        .header-menu-drawer-inner {
          height: 100%;
          min-height: 100%;
        }
        .header-drawer-link {
          color: rgba(255, 255, 255, 0.92);
          text-align: left;
        }
        .header-drawer-link:hover,
        .header-drawer-link:focus-visible {
          color: #fff;
        }
        .header-drawer-link--child {
          color: rgba(255, 255, 255, 0.72);
          letter-spacing: 0.12em;
        }
        .header-drawer-link--child:hover,
        .header-drawer-link--child:focus-visible {
          color: #fff;
        }
        .header-drawer-link--accent {
          color: ${HEADER_CTA};
          font-weight: 600;
        }
        .header-drawer-link--accent:hover,
        .header-drawer-link--accent:focus-visible {
          color: color-mix(in srgb, ${HEADER_CTA} 80%, white);
        }
        .header-drawer-group-toggle {
          border: 0;
          cursor: pointer;
        }
        .header-drawer-preview-body {
          width: 100%;
          max-width: 36rem;
        }
        .header-drawer-preview-heading {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #fff;
          margin: 0;
        }
        .header-drawer-preview-subhead {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-style: italic;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.78);
          margin: 0 0 0.35rem;
        }
        .header-drawer-preview-copy {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.84);
          margin: 0;
        }
        .header-drawer-preview-list {
          margin: 0.85rem 0 0;
          padding: 0 0 0 1.1rem;
          list-style: disc;
        }
        .header-drawer-preview-list li {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.84);
          margin: 0.2rem 0;
        }
        .header-drawer-contact-links {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-top: 1rem;
        }
        .header-drawer-contact-icon {
          color: ${HEADER_CTA};
        }
        .header-drawer-hours {
          margin: 0.85rem 0 0;
          max-width: 22rem;
        }
        .header-drawer-hours-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 0.2rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.45;
        }
        .header-drawer-hours-row dt {
          color: ${HEADER_CTA};
          font-weight: 500;
        }
        .header-drawer-hours-row dd {
          margin: 0;
          color: rgba(255, 255, 255, 0.84);
          white-space: nowrap;
        }
        .header-drawer-inline-link {
          color: #fff;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .header-drawer-inline-link:hover {
          color: color-mix(in srgb, ${HEADER_CTA} 70%, white);
        }
        .header-drawer-inline-link--accent {
          color: ${HEADER_CTA};
        }
        .header-drawer-inline-link--accent:hover {
          color: color-mix(in srgb, ${HEADER_CTA} 80%, white);
        }
        .header-drawer-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 1.15rem;
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #0a0a0a;
          background: #fff;
          border: 1px solid #fff;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .header-drawer-cta:hover {
          background: transparent;
          color: #fff;
        }
        .header-drawer-cta--ghost {
          color: #fff;
          background: transparent;
        }
        .header-drawer-cta--ghost:hover {
          background: var(--vibe-color-primary);
          border-color: #fff;
          color: #fff;
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.pay_rent_link': {
      label: 'Pay Your Rent Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Pay Your Rent' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.maintenance_request_link': {
      label: 'Maintenance Request Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Submit a Maintenance Request' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.qualifying_criteria_link': {
      label: 'Qualifying Criteria Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: 'https://irp.cdn-website.com/c035667c/files/uploaded/Weidner_Qualifying_Criteria_Seattle.pdf',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_google_place_url',
          suggestedFields: ['property_google_place_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.view_floor_plans_link': {
      label: 'View Floor Plans Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
